/** RaiderMapCR - Interactive campus map with room selection and occupancy display */
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { 
  Box, 
  Paper, 
  Typography, 
  CircularProgress, 
  ToggleButtonGroup, 
  ToggleButton,
  Chip,
  Grid,
  IconButton,
  Divider,
  useMediaQuery,
  ClickAwayListener,
  Collapse,
  Slide,
  List,
  ListItem
} from "@mui/material";
import { 
  People as PeopleIcon,
  Category as CategoryIcon,
  Layers as LayersIcon,
  KeyboardArrowUp as UpArrowIcon,
  AccessTime as AccessTimeIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useFloorPlan from "../hooks/useFloorPlan";
import { 
  convertTo12Hour, 
  getCurrentDayOfWeek, 
  findOccupiedUntilTime,
  findNextOccupiedTime,
  getWeekdayOptions
} from "../utils/timeUtils";

const MapPage = () => {
  const navigate = useNavigate();
  
  const getInitialFloor = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const roomParam = urlParams.get('room');
    if (roomParam) {
      return "F1";
    }
    return "F1";
  };

  const [currentFloor, setCurrentFloor] = useState(getInitialFloor());
  const [viewMode, setViewMode] = useState("occupancy");
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomPopupVisible, setRoomPopupVisible] = useState(false);
  const [previousRoom, setPreviousRoom] = useState(null);
  const [showPreviousRoom, setShowPreviousRoom] = useState(false);
  const [previousRoomSliding, setPreviousRoomSliding] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [hoveredRoomId, setHoveredRoomId] = useState(null);
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [isSearchingRoom, setIsSearchingRoom] = useState(false);
  const [selectedWeekday, setSelectedWeekday] = useState(getCurrentDayOfWeek());
  const [roomSchedule, setRoomSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [occupiedUntilTime, setOccupiedUntilTime] = useState(null);
  const [nextOccupiedTime, setNextOccupiedTime] = useState(null);
  const [allRoomSchedules, setAllRoomSchedules] = useState(null);
  const [schedulesLoaded, setSchedulesLoaded] = useState(false);
  const [occupancyUpdateComplete, setOccupancyUpdateComplete] = useState(false);
  const svgRef = useRef(null);
  const hoveredRoomIdRef = useRef(null);
  const popupRef = useRef(null);
  const transitionTimeoutRef = useRef(null);
  const cleanupTimeoutRef = useRef(null);
  
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  const hasUpdatedOccupancy = useRef(false);
  const hasProcessedInitialRoom = useRef(false);
  const hasCompletedInitialSearch = useRef(false);
  const hasScrolledToF1 = useRef(false);

  const hasValidStatus = (room) => {
    return room && room.status && room.status !== 'unknown' && room.status.trim() !== '';
  };

  const hasValidCapacity = (room) => {
    return room && room.capacity && room.capacity > 0;
  };

  const hasValidDescription = (room) => {
    return room && room.description && room.description.trim() !== '';
  };

  const preloadAllRoomSchedules = useCallback(async () => {
    if (schedulesLoaded) return;
    
    try {
      const response = await fetch('/api/schedules?type=rooms');
      if (response.ok) {
        const data = await response.json();
        setAllRoomSchedules(data.schedules || {});
        setSchedulesLoaded(true);
      } else {
        console.error('Failed to preload room schedules:', response.statusText);
        setSchedulesLoaded(true); // Mark as loaded even on error to prevent retries
      }
    } catch (error) {
      console.error('Error preloading room schedules:', error);
      setSchedulesLoaded(true); // Mark as loaded even on error to prevent retries
    }
  }, [schedulesLoaded]);

  const getRoomScheduleFromCache = useCallback((roomAlias) => {
    if (!allRoomSchedules || !roomAlias) return null;
    return allRoomSchedules[roomAlias] || null;
  }, [allRoomSchedules]);

  const fetchRoomSchedule = useCallback(async (roomAlias) => {
    if (!roomAlias) return;
    
    setScheduleLoading(true);
    
    if (schedulesLoaded && allRoomSchedules) {
      const cachedSchedule = getRoomScheduleFromCache(roomAlias);
      if (cachedSchedule) {
        setRoomSchedule(cachedSchedule);
        const currentDay = getCurrentDayOfWeek();
        const todaySchedule = cachedSchedule[currentDay] || [];
        const occupiedUntil = findOccupiedUntilTime(todaySchedule);
        const nextOccupied = findNextOccupiedTime(todaySchedule);
        setOccupiedUntilTime(occupiedUntil);
        setNextOccupiedTime(nextOccupied);
        setScheduleLoading(false);
        return;
      }
    }
    console.warn('No schedule data available for room:', roomAlias);
    setRoomSchedule({});
    setOccupiedUntilTime(null);
    setNextOccupiedTime(null);
    setScheduleLoading(false);
  }, [schedulesLoaded, allRoomSchedules, getRoomScheduleFromCache]);

  const handleWeekdayChange = (event, newWeekday) => {
    if (newWeekday !== null) {
      setSelectedWeekday(newWeekday);
    }
  };

  const handleClassCodeClick = (coursePrefix, courseNumber) => {
    if (coursePrefix && courseNumber) {
      const classCode = `${coursePrefix} ${courseNumber}`;
      navigate(`/schedule?search=${encodeURIComponent(classCode)}`);
    }
  };

  
  const {
    svgContent,
    rooms,
    isLoaded,
    error,
    applySvgColors,
    applyLabelColors,
    makePathsVisible,
    highlightRoom,
    unhighlightRoom,
    allFloorsData,
    isFullyLoaded,
    refreshRoomData
  } = useFloorPlan(currentFloor);

  const isMapReady = isLoaded && occupancyUpdateComplete;

  useEffect(() => {
    preloadAllRoomSchedules();
  }, [preloadAllRoomSchedules]);

  useEffect(() => {
    if (!isFullyLoaded || !refreshRoomData || hasUpdatedOccupancy.current) return;

    hasUpdatedOccupancy.current = true;
    let isMounted = true;

    const updateRoomOccupancy = async () => {
      try {
        const response = await fetch('/api/update-room-occupancy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        if (isMounted && response.ok) {
          await refreshRoomData();
        }
      } catch (error) {
      } finally {
        if (isMounted) setOccupancyUpdateComplete(true);
      }
    };

    updateRoomOccupancy();
    return () => { isMounted = false; };
  }, [isFullyLoaded, refreshRoomData]);

  const handleFloorChange = (event, newFloor) => {
    if (newFloor !== null && newFloor !== currentFloor) {
      hoveredRoomIdRef.current = null;
      setHoveredRoomId(null);
      setCurrentFloor(newFloor);
    }
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };


  const handleMenuToggle = () => {
    if (menuOpen) {
      setMenuOpen(false);
      setTimeout(() => setMenuVisible(false), 200);
    } else {
      setMenuVisible(true);
      setTimeout(() => setMenuOpen(true), 10);
    }
  };

  const handleMenuClose = () => {
    if (menuOpen) {
      setMenuOpen(false);
      setTimeout(() => setMenuVisible(false), 200);
    }
  };

  const manageSvgLayering = useCallback((svgElement, hoveredRoomId = null, selectedRoomId = null) => {
    if (!svgElement || !rooms.length) return;
    const hasHighlightedRoom = svgElement.querySelector('.room-path.highlighted');
    if (hasHighlightedRoom) return;
    // Only work with elements that exist in current SVG (prevents floor-switch bugs)
    const currentSvgPathIds = Array.from(svgElement.querySelectorAll('path')).map(p => p.id).filter(id => id);
    const validRooms = rooms.filter(room => currentSvgPathIds.includes(room.id));
    if (validRooms.length === 0) return;
    const currentOrder = Array.from(svgElement.querySelectorAll('path')).map(p => p.id);
    const interactiveRooms = validRooms.filter(r => r.type !== "structure").map(r => r.id);
    const structureRooms = validRooms.filter(r => r.type === "structure").map(r => r.id);
    const interactiveRoomsInOrder = interactiveRooms.every(roomId => {
      const roomIndex = currentOrder.indexOf(roomId);
      const structureIndexes = structureRooms.map(sId => currentOrder.indexOf(sId)).filter(i => i >= 0);
      return roomIndex >= 0 && structureIndexes.every(sIndex => sIndex < roomIndex);
    });
    if (interactiveRoomsInOrder && !selectedRoomId && !hoveredRoomId) {
      return;
    }
    const iconsOverlay = svgElement.querySelector('#icons-overlay');
    const labelsOverlay = svgElement.querySelector('#labels-overlay');
    const roomElements = validRooms.map(room => {
      const pathElement = svgElement.querySelector(`#${room.id}`);
      if (pathElement) {
        return { element: pathElement, room };
      }
      return null;
    }).filter(Boolean);
    roomElements.sort((a, b) => {
      // Hovered room on top first so its stroke is visible over adjacent rooms (including selected)
      if (a.room.id === hoveredRoomId && a.room.id !== selectedRoomId) return 1;
      if (b.room.id === hoveredRoomId && b.room.id !== selectedRoomId) return -1;
      if (a.room.id === selectedRoomId) return 1;
      if (b.room.id === selectedRoomId) return -1;
      if (a.room.type !== "structure" && b.room.type === "structure") return 1;
      if (a.room.type === "structure" && b.room.type !== "structure") return -1;
      if (a.room.type !== "structure" && b.room.type !== "structure") return 0;
      if (a.room.type === "structure" && b.room.type === "structure") return 0;
      
      return 0;
    });
    roomElements.forEach(({ element }) => {
      const firstOverlay = iconsOverlay || labelsOverlay;
      if (firstOverlay) {
        svgElement.insertBefore(element, firstOverlay);
      } else {
        svgElement.appendChild(element);
      }
    });
    if (iconsOverlay && labelsOverlay) {
      svgElement.appendChild(iconsOverlay);
      svgElement.appendChild(labelsOverlay);
    } else if (labelsOverlay) {
      svgElement.appendChild(labelsOverlay);
    } else if (iconsOverlay) {
      svgElement.appendChild(iconsOverlay);
    }
    

  }, [rooms]);

  const handleRoomClick = useCallback((roomId) => {
    const room = rooms.find(r => r.id === roomId);
    if (room) {
      if (selectedRoom && selectedRoom.id === roomId) {
        return;
      }

      const svgElement = svgRef.current?.querySelector('svg');
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }
      setShowPreviousRoom(false);
      setPreviousRoom(null);
      setPreviousRoomSliding(false);
      setIsTransitioning(false);

      if (selectedRoom) {
        setIsTransitioning(true);
        if (svgElement) {
          unhighlightRoom(selectedRoom.id, svgElement, viewMode);
        }
        setPreviousRoom(selectedRoom);
        setShowPreviousRoom(true);
        setPreviousRoomSliding(true);
        setRoomPopupVisible(false);
        transitionTimeoutRef.current = setTimeout(() => {
          setShowPreviousRoom(false);
          setPreviousRoom(null);
          setPreviousRoomSliding(false);
          setSelectedRoom(room);
          setHoveredRoomId(null);
          manageSvgLayering(svgElement, null, room.id);
          if (svgElement) {
            highlightRoom(room.id, svgElement, viewMode);
          }
          setRoomPopupVisible(true);
          setIsTransitioning(false);
          
          transitionTimeoutRef.current = null;
        }, 200);
      } else {
        setSelectedRoom(room);
        setHoveredRoomId(null);
        manageSvgLayering(svgElement, null, room.id);
        if (svgElement) {
          highlightRoom(room.id, svgElement, viewMode);
        }
        
        setRoomPopupVisible(true);
      }
    }
  }, [rooms, selectedRoom, highlightRoom, unhighlightRoom, viewMode, manageSvgLayering]);

  const handleSvgClick = useCallback((event) => {
    const clickedElement = event.target;
    const tagName = clickedElement.tagName?.toLowerCase();
    if (tagName === 'path' && clickedElement.id) {
      const room = rooms.find(r => r.id === clickedElement.id);

      if (room && room.type !== "structure") {
        event.stopPropagation();
        event.preventDefault();
        handleRoomClick(room.id);
        return;
      }
    }
    const elementsAtPoint = document.elementsFromPoint(event.clientX, event.clientY);

    for (const element of elementsAtPoint) {
      if (element.tagName?.toLowerCase() === 'path' && element.id) {
        const room = rooms.find(r => r.id === element.id);
        if (room && room.type !== "structure") {
          event.stopPropagation();
          event.preventDefault();
          handleRoomClick(room.id);
          return;
        }
      }
    }
  }, [rooms, handleRoomClick]);

  useLayoutEffect(() => {
    const svgElement = svgRef.current?.querySelector('svg');
    if (svgElement && rooms.length > 0) {
      applyLabelColors(svgElement, viewMode, rooms, hoveredRoomId, selectedRoom?.id);
    }
  }, [hoveredRoomId, selectedRoom?.id, viewMode, rooms, applyLabelColors, svgContent]);

  useLayoutEffect(() => {
    const svgElement = svgRef.current?.querySelector('svg');
    if (!svgElement) return;

    svgElement.querySelectorAll('.room-path.hovered').forEach(el => {
      el.classList.remove('hovered');
    });

    if (hoveredRoomId) {
      const hoveredPath = svgElement.querySelector(`#${hoveredRoomId}`);
      if (hoveredPath && !hoveredPath.classList.contains('structure') && !hoveredPath.classList.contains('highlighted')) {
        hoveredPath.classList.add('hovered');
      }
    }
  }, [hoveredRoomId]);

  useLayoutEffect(() => {
    const svgElement = svgRef.current?.querySelector('svg');
    if (svgElement && rooms.length > 0) {
      manageSvgLayering(svgElement, hoveredRoomId, selectedRoom?.id);
    }
  }, [hoveredRoomId, selectedRoom?.id, rooms, manageSvgLayering]);

  const resolveHoveredRoomId = useCallback((clientX, clientY) => {
    const svgElement = svgRef.current?.querySelector('svg');
    if (!svgElement) return null;

    const labelsOverlay = svgElement.querySelector('#labels-overlay');
    if (labelsOverlay) {
      for (const textEl of labelsOverlay.querySelectorAll('text')) {
        const roomId = textEl.getAttribute('data-room-id');
        if (!roomId) continue;
        const room = rooms.find(r => r.id === roomId && r.type !== 'structure');
        if (!room) continue;
        const rect = textEl.getBoundingClientRect();
        if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
          return roomId;
        }
      }
    }

    const elements = document.elementsFromPoint(clientX, clientY);
    const path = elements.find(el => el.tagName === 'path' && el.id && rooms.some(r => r.id === el.id && r.type !== 'structure'));
    return path?.id ?? null;
  }, [rooms]);

  const handleRoomHover = useCallback((event) => {
    if (isMobile) return;
    const svgElement = svgRef.current?.querySelector('svg');
    if (!svgElement) return;

    const roomId = resolveHoveredRoomId(event.clientX, event.clientY);
    if (!roomId) return;

    const pathIds = Array.from(svgElement.querySelectorAll('path')).map(p => p.id).filter(Boolean);
    if (!pathIds.includes(roomId)) return;

    hoveredRoomIdRef.current = roomId;
    setHoveredRoomId(roomId);
  }, [resolveHoveredRoomId, isMobile]);

  const handleRoomHoverEnd = useCallback((event) => {
    if (isMobile) return;
    const svgElement = svgRef.current?.querySelector('svg');
    if (!svgElement) return;

    const hoveredElement = event.target;
    const isLeavingPath = hoveredElement.tagName === 'path' && hoveredElement.id;
    const isLeavingSvg = hoveredElement.tagName === 'svg' || hoveredElement.tagName === 'SVG';
    if (!isLeavingPath && !isLeavingSvg) return;

    const related = event.relatedTarget;
    if (related && related.tagName === 'path' && related.id) {
      const room = rooms.find(r => r.id === related.id && r.type !== 'structure');
      if (room) {
        const pathIds = Array.from(svgElement.querySelectorAll('path')).map(p => p.id).filter(Boolean);
        if (pathIds.includes(related.id)) {
          hoveredRoomIdRef.current = related.id;
          setHoveredRoomId(related.id);
          return;
        }
      }
    }

    const roomIdNow = resolveHoveredRoomId(event.clientX, event.clientY);
    if (roomIdNow) {
      hoveredRoomIdRef.current = roomIdNow;
      setHoveredRoomId(roomIdNow);
    } else {
      hoveredRoomIdRef.current = null;
      setHoveredRoomId(null);
    }
  }, [rooms, resolveHoveredRoomId, isMobile]);

  const handleMapMouseLeave = useCallback(() => {
    if (isMobile) return;
    hoveredRoomIdRef.current = null;
    setHoveredRoomId(null);
  }, [isMobile]);

  const handleMapMouseMove = useCallback((event) => {
    if (isMobile) return;
    const currentRoomId = resolveHoveredRoomId(event.clientX, event.clientY);
    if (currentRoomId === hoveredRoomIdRef.current) return;

    hoveredRoomIdRef.current = currentRoomId || null;
    setHoveredRoomId(currentRoomId || null);
  }, [resolveHoveredRoomId, isMobile]);

  const handleRoomClose = useCallback(() => {
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }
    
    if (selectedRoom && svgRef.current) {
      const svgElement = svgRef.current.querySelector('svg');
      if (svgElement) {
        unhighlightRoom(selectedRoom.id, svgElement, viewMode);
        hoveredRoomIdRef.current = null;
        setHoveredRoomId(null);
        manageSvgLayering(svgElement, null, null);
      }
    }
    
    setRoomPopupVisible(false);
    setIsPanelExpanded(false); // Reset expanded state when closing
    setTimeout(() => {
      setSelectedRoom(null);
      setRoomPopupVisible(false);
      setShowPreviousRoom(false);
      setPreviousRoom(null);
      setPreviousRoomSliding(false);
      setIsTransitioning(false);
    }, 300);
  }, [selectedRoom, viewMode, unhighlightRoom, manageSvgLayering]);

  const handlePanelToggle = useCallback(() => {
    setIsPanelExpanded(prev => !prev);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectedRoom && popupRef.current && !popupRef.current.contains(event.target)) {
        if (showPreviousRoom || isTransitioning) {
          return;
        }
        const clickedElement = event.target;
        const isRoomPath = clickedElement.tagName === 'path' && clickedElement.id && rooms.some(room => room.id === clickedElement.id);
        if (isRoomPath) {
          return;
        }
        const mapContainer = svgRef.current;
        if (mapContainer && (mapContainer.contains(event.target) || event.target.closest('.main-content'))) {
          handleRoomClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedRoom, showPreviousRoom, isTransitioning, rooms, handleRoomClose]);

  const searchRoomAcrossFloors = useCallback(async (roomName) => {
    if (!isFullyLoaded) {
      const checkLoaded = () => {
        return new Promise((resolve) => {
          const interval = setInterval(() => {
            if (isFullyLoaded) {
              clearInterval(interval);
              resolve();
            }
          }, 100);
          setTimeout(() => {
            clearInterval(interval);
            resolve();
          }, 10000);
        });
      };
      await checkLoaded();
    }

    setIsSearchingRoom(true);
    const floors = ['F1', 'F3', 'S1'];
    for (const floor of floors) {
      const floorData = allFloorsData[floor];
      if (!floorData) continue;

      const { roomData } = floorData;
      const targetRoom = Object.values(roomData.rooms || {}).find(room => 
        room.alias === roomName || room.name === roomName
      );
      if (targetRoom) {
        setCurrentFloor(floor);
        handleRoomClick(targetRoom.path_id.replace(`${floor}_`, ''));
        setIsSearchingRoom(false);
        return;
      }
    }
    setIsSearchingRoom(false);
  }, [isFullyLoaded, allFloorsData, handleRoomClick]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const roomParam = urlParams.get('room');
    
    if (roomParam && !hasProcessedInitialRoom.current && isFullyLoaded) {
      hasProcessedInitialRoom.current = true;
      searchRoomAcrossFloors(roomParam);
    }
  }, [searchRoomAcrossFloors, isFullyLoaded]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const roomParam = urlParams.get('room');
    if (roomParam && rooms.length > 0 && hasProcessedInitialRoom.current && !hasCompletedInitialSearch.current) {
      const targetRoom = rooms.find(room => 
        room.alias === roomParam || room.name === roomParam
      );
      if (targetRoom && !selectedRoom) {
        handleRoomClick(targetRoom.id);
        hasCompletedInitialSearch.current = true;
      }
    }
  }, [rooms, currentFloor, handleRoomClick, selectedRoom]);

  useEffect(() => {
    if (selectedRoom && (selectedRoom.type === 'laboratory' || selectedRoom.type === 'classroom')) {
      const roomIdentifier = selectedRoom.alias || selectedRoom.name;
      if (roomIdentifier) {
        fetchRoomSchedule(roomIdentifier);
      }
    } else {
      setRoomSchedule(null);
      setOccupiedUntilTime(null);
      setNextOccupiedTime(null);
    }
  }, [selectedRoom, fetchRoomSchedule]);

  useEffect(() => {
    if (currentFloor === "F1" && isMapReady && !hasScrolledToF1.current) {
      hasScrolledToF1.current = true;
      const delay = isMobile ? 700 : 500;
      setTimeout(() => {
        const rootElement = document.getElementById('root');
        if (!rootElement) return;
        const maxScrollTop = rootElement.scrollHeight - rootElement.clientHeight;
        rootElement.scrollTo({
          top: maxScrollTop,
          behavior: 'smooth'
        });
        setTimeout(() => {
          if (Math.abs(rootElement.scrollTop - maxScrollTop) > 10) {
            rootElement.scrollTop = maxScrollTop;
          }
        }, 600);
      }, delay);
    }
  }, [currentFloor, isMapReady, isMobile, svgContent]);

  useEffect(() => {
    const currentSvgRef = svgRef.current;
    if (currentSvgRef && svgContent) {
      if (rooms.length > 0) {
        applySvgColors(currentSvgRef, viewMode, rooms);
        // requestAnimationFrame ensures SVG DOM is ready (Chrome/Edge/Vivaldi)
        const svgElement = currentSvgRef.querySelector('svg');
        if (svgElement) {
          svgElement.removeEventListener('click', handleSvgClick);
          svgElement.removeEventListener('mouseover', handleRoomHover);
          svgElement.removeEventListener('mouseout', handleRoomHoverEnd);
          currentSvgRef.removeEventListener('mouseleave', handleMapMouseLeave);
          currentSvgRef.removeEventListener('mousemove', handleMapMouseMove);
          requestAnimationFrame(() => {
            if (!svgElement.isConnected) return;
            svgElement.addEventListener('click', handleSvgClick);
            if (!isMobile) {
              svgElement.addEventListener('mouseover', handleRoomHover);
              svgElement.addEventListener('mouseout', handleRoomHoverEnd);
              currentSvgRef.addEventListener('mouseleave', handleMapMouseLeave);
              currentSvgRef.addEventListener('mousemove', handleMapMouseMove);
            }
          });
        }
        const svgElementForLayering = currentSvgRef.querySelector('svg');
        if (svgElementForLayering) {
          const currentSvgPathIds = Array.from(svgElementForLayering.querySelectorAll('path')).map(p => p.id).filter(id => id);
          const validRooms = rooms.filter(room => currentSvgPathIds.includes(room.id));
          const roomElements = validRooms.map(room => {
            const pathElement = currentSvgRef.querySelector(`#${room.id}`);
            if (pathElement) {
              return { element: pathElement, room };
            }
            return null;
          }).filter(Boolean);
          roomElements.forEach(({ element, room }) => {
            // Remove fill="none" - Edge/Chrome block pointer events on paths with it
            if (element.getAttribute('fill') === 'none') {
              element.removeAttribute('fill');
            }
            element.classList.remove('room-path', 'structure', 'interactive-room', 'highlighted', 'hovered');
            element.classList.add('room-path');
            if (room.type === "structure") {
              element.classList.add('structure');
              element.style.cursor = "default";
              element.style.pointerEvents = "none";
            } else {
              element.classList.add('interactive-room');
              element.style.cursor = "pointer";
              element.style.pointerEvents = "all";
              element.style.strokeWidth = "";
              element.style.filter = "";
              element.style.transition = "";
            }
          });
          manageSvgLayering(svgElementForLayering, null, selectedRoom?.id);
          if (selectedRoom) {
            highlightRoom(selectedRoom.id, svgElementForLayering, viewMode);
          }
          
          applyLabelColors(svgElementForLayering, viewMode, rooms, null, selectedRoom?.id);
        }
      } else {
        makePathsVisible(currentSvgRef);
      }
    }
    return () => {
      if (currentSvgRef) {
        const svgElement = currentSvgRef.querySelector('svg');
        if (svgElement) {
          svgElement.removeEventListener('click', handleSvgClick);
          if (!isMobile) {
            svgElement.removeEventListener('mouseover', handleRoomHover);
            svgElement.removeEventListener('mouseout', handleRoomHoverEnd);
            currentSvgRef.removeEventListener('mouseleave', handleMapMouseLeave);
            currentSvgRef.removeEventListener('mousemove', handleMapMouseMove);
          }
        }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svgContent, rooms, viewMode, applySvgColors, applyLabelColors, makePathsVisible, handleSvgClick, handleRoomHover, handleRoomHoverEnd, handleMapMouseLeave, handleMapMouseMove, manageSvgLayering, selectedRoom, isTransitioning, isMobile, isIOS, highlightRoom]);

  const getPopupWrapperStyle = () => {
    if (isMobile) {
      return {
        position: "fixed",
        bottom: 70,
        left: 0,
        right: 0,
        zIndex: 1001,
        pointerEvents: "none"
      };
    } else {
      return {
        position: "fixed",
        bottom: 0,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1001,
        pointerEvents: "none"
      };
    }
  };

  const getPopupStyle = () => {
    const baseStyle = {
      backgroundColor: 'rgba(42, 42, 42, 0.5)',
      border: '1px solid rgba(74, 74, 74, 0.4)',
      backdropFilter: 'blur(12px)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      color: '#ffffff',
      pointerEvents: "auto"
    };
    if (isMobile) {
      return {
        ...baseStyle,
        width: "100%",
        maxWidth: "100%",
        borderRadius: "16px 16px 0 0",
        p: 1.5
      };
    } else {
      return {
        ...baseStyle,
        maxWidth: "550px",
        width: "550px",
        borderRadius: "8px 8px 0 0",
        p: 2
      };
    }
  };

  const renderLoadingState = () => (
    <Box 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      height="60vh"
      minHeight="400px"
    >
      <CircularProgress />
    </Box>
  );

  if (error) {
    return (
      <div className="main-content" style={{ 
        paddingTop: isMobile ? '72px' : '80px',
        paddingBottom: isMobile ? '80px' : '0',
        position: 'relative',
        textAlign: 'left',
        background: 'linear-gradient(180deg, #1a1a1a 0%, #0f0f0f 100%)',
        minHeight: '100vh'
      }}>
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          height={isMobile 
            ? "calc(100vh - 72px - 80px - 40px)" 
            : "calc(100vh - 80px)"
          }
          flexDirection="column"
        >
          <Typography variant="h6" color="error" gutterBottom>
            Error Loading Map Data
          </Typography>
          <Typography variant="body2" sx={{ color: '#ffffff' }}>
            {error}
          </Typography>
        </Box>
      </div>
    );
  }


  return (
    <div className="main-content" style={{ 
      paddingTop: isMobile ? '72px' : '80px',
      paddingBottom: isMobile ? '60px' : '0',
      position: 'relative',
      textAlign: 'left',
      background: 'linear-gradient(180deg, #1a1a1a 0%, #0f0f0f 100%)',
      minHeight: '100vh'
    }}>
      <Box sx={{ 
        minHeight: isMobile 
          ? "calc(100vh - 72px - 60px - 40px)" 
          : "calc(100vh - 80px)", 
        position: "relative"
      }}>
        <Box
          sx={{
            position: "fixed",
            top: isMobile ? 82 : 104, // Mobile: 82px, Desktop: 104px
            left: isMobile ? "40px" : "calc(50% - 390px)", // Left side on mobile, left of center on desktop
            zIndex: 1000,
          }}
        >
          <ToggleButtonGroup
            value={currentFloor}
            exclusive
            onChange={handleFloorChange}
            orientation={isMobile ? "horizontal" : "vertical"}
            size="small"
            disabled={!isMapReady}
            sx={{ 
              '& .MuiToggleButton-root': {
                fontWeight: 'bold',
                minWidth: isMobile ? '50px' : '60px',
                height: isMobile ? '48px' : '40px',
                color: '#ffffff',
                backgroundColor: 'rgba(42, 42, 42, 0.85)',
                border: '1px solid rgba(74, 74, 74, 0.6)', // Match layers menu button
                backdropFilter: 'blur(12px)', // Match layers menu button
                boxShadow: '0 0 30px rgba(0, 0, 0, 0.2)', // Match layers menu button
                borderRadius: 0,
                ...(!isMobile && {
                  '&:first-of-type': {
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                  },
                  '&:last-of-type': {
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                  },
                }),
                ...(isMobile && {
                  '&:first-of-type': {
                    borderTopLeftRadius: 8,
                    borderBottomLeftRadius: 8,
                  },
                  '&:last-of-type': {
                    borderTopRightRadius: 8,
                    borderBottomRightRadius: 8,
                  },
                }),
                ...(!isMobile && {
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#ffffff',
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  },
                }),
                '@media (hover: none)': {
                  '&:hover': {
                    backgroundColor: 'rgba(42, 42, 42, 0.85)',
                    borderColor: 'rgba(74, 74, 74, 0.6)',
                  },
                },
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  borderColor: 'primary.main',
                  ...(!isMobile && {
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                      borderColor: 'primary.dark'
                    }
                  }),
                  '@media (hover: none)': {
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      borderColor: 'primary.main',
                    },
                  },
                },
                '&.Mui-disabled': {
                  color: '#ffffff !important',
                  fontWeight: 'bold !important',
                  opacity: '0.6'
                }
              }
            }}
          >
            <ToggleButton value={"F3"}>F3</ToggleButton>
            <ToggleButton value={"F1"}>F1</ToggleButton>
            <ToggleButton value={"S1"}>S1</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <ClickAwayListener onClickAway={handleMenuClose}>
          <Box
            sx={{
              position: "fixed",
              top: isMobile ? 82 : 104, // Mobile: 82px, Desktop: 104px
              right: isMobile ? "40px" : "calc(50% - 350px)", // Right side on mobile, right of center on desktop
              zIndex: 1000,
            }}
          >
            <Box sx={{ position: 'relative' }}>
              <IconButton
                onClick={handleMenuToggle}
                sx={{
                  width: isMobile ? 48 : 40,
                  height: isMobile ? 48 : 40,
                  backgroundColor: 'rgba(42, 42, 42, 0.85)',
                  borderRadius: menuOpen ? '12px 12px 0 0' : 2,
                  border: '1px solid rgba(74, 74, 74, 0.6)',
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 0 30px rgba(0, 0, 0, 0.2)',
                  transition: 'border-radius 150ms ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  },
                  '&:active': {
                    backgroundColor: 'rgba(42, 42, 42, 0.85)' // Prevent pressed state on click
                  },
                  '&:focus': {
                    backgroundColor: 'rgba(42, 42, 42, 0.85)' // Prevent focus state
                  }
                }}
              >
                <LayersIcon 
                  sx={{
                    color: '#ffffff'
                  }}
                />
              </IconButton>

              {menuVisible && (
                <Paper
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    width: isMobile ? 260 : 280,
                    p: isMobile ? 1.25 : 1.5,
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 0,
                    backgroundColor: 'rgba(42, 42, 42, 0.75)',
                    border: '1px solid rgba(74, 74, 74, 0.6)',
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                    borderTop: 'none',
                    backdropFilter: 'blur(12px)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    maxWidth: '100vw',
                    overflow: 'hidden',
                    transformOrigin: 'top right',
                    transform: menuOpen ? 'scale(1)' : 'scale(0)',
                    opacity: menuOpen ? 1 : 0,
                    transition: 'transform 0.2s ease-out, opacity 0.2s ease-out',
                    '@keyframes expandFromTopRight': {
                      '0%': {
                        transform: 'scale(0)',
                        transformOrigin: 'top right',
                        opacity: 0
                      },
                      '30%': {
                        opacity: 0.3
                      },
                      '100%': {
                        transform: 'scale(1)',
                        transformOrigin: 'top right',
                        opacity: 1
                      }
                    }
                  }}
                >
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                    fullWidth
                    sx={{ 
                      mb: 1.5,
                      '& .MuiToggleButton-root': {
                        color: '#ffffff',
                        backgroundColor: 'transparent',
                        border: '1px solid rgba(74, 74, 74, 0.5)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          borderColor: 'rgba(255, 255, 255, 0.3)'
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          borderColor: 'primary.main',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                            borderColor: 'primary.dark'
                          }
                        }
                      }
                    }}
                  >
                    <ToggleButton value={"type"}>
                      <CategoryIcon sx={{ mr: 1, fontSize: 16 }} />
                      Type
                    </ToggleButton>
                    <ToggleButton value={"occupancy"}>
                      <PeopleIcon sx={{ mr: 1, fontSize: 16 }} />
                      Occupancy
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <Divider sx={{ my: 1, borderColor: 'rgba(74, 74, 74, 0.5)' }} />

                  <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 'medium', color: '#ffffff' }}>
                    Legend
                  </Typography>
                  <Box sx={{ 
                    display: "grid", 
                    gridTemplateColumns: "1fr 1fr", 
                    gap: 1, 
                  }}>
                    {viewMode === "occupancy" ? (
                      <>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ width: 12, height: 12, backgroundColor: "#A8DDA8", border: "1px solid #4A4A4ACC", borderRadius: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#ffffff' }}>Available</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ width: 12, height: 12, backgroundColor: "#FFB3B3", border: "1px solid rgba(74, 74, 74, 0.8)", borderRadius: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#ffffff' }}>Occupied</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ width: 12, height: 12, backgroundColor: "#D4C5E2", border: "1px solid rgba(74, 74, 74, 0.8)", borderRadius: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#ffffff' }}>Unavailable</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ width: 12, height: 12, backgroundColor: "#9B9B9B", border: "1px solid rgba(74, 74, 74, 0.8)", borderRadius: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#ffffff' }}>Unknown</Typography>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ width: 12, height: 12, backgroundColor: "#87CEEB", border: "1px solid rgba(74, 74, 74, 0.8)", borderRadius: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#ffffff' }}>Laboratory</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ width: 12, height: 12, backgroundColor: "#FFCB80", border: "1px solid rgba(74, 74, 74, 0.8)", borderRadius: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#ffffff' }}>Classroom</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ width: 12, height: 12, backgroundColor: "#E6E6FA", border: "1px solid rgba(74, 74, 74, 0.8)", borderRadius: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#ffffff' }}>Office</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ width: 12, height: 12, backgroundColor: "#FFFACD", border: "1px solid rgba(74, 74, 74, 0.8)", borderRadius: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#ffffff' }}>Study</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ width: 12, height: 12, backgroundColor: "#F0E68C", border: "1px solid rgba(74, 74, 74, 0.8)", borderRadius: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#ffffff' }}>Bathroom</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ width: 12, height: 12, backgroundColor: "#B0C4DE", border: "1px solid rgba(74, 74, 74, 0.8)", borderRadius: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#ffffff' }}>Stairs</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ width: 12, height: 12, backgroundColor: "#D2B48C", border: "1px solid rgba(74, 74, 74, 0.8)", borderRadius: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#ffffff' }}>Elevator</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ width: 12, height: 12, backgroundColor: "#DDA0DD", border: "1px solid rgba(74, 74, 74, 0.8)", borderRadius: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#ffffff' }}>Service</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ width: 12, height: 12, backgroundColor: "#98D8C8", border: "1px solid rgba(74, 74, 74, 0.8)", borderRadius: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#ffffff' }}>Other</Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                </Paper>
              )}
            </Box>
          </Box>
        </ClickAwayListener>

        <Box
          sx={{
            minHeight: "100%",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: isMobile ? "center" : "flex-start",
            paddingTop: isMobile ? "70px" : "20px",
            paddingBottom: "20px",
            paddingLeft: isMobile ? "20px" : "120px",
            paddingRight: isMobile ? "20px" : "120px",
            boxSizing: "border-box"
          }}
        >
          {!isMapReady ? (
            renderLoadingState()
          ) : svgContent ? (
            <Box
              ref={svgRef}
              sx={{
                maxWidth: isMobile ? "90%" : "100%",
                transform: "scale(1)",
                transformOrigin: "top center",
                transition: "all 0.3s ease-in-out, opacity 0.2s ease-in-out",
                opacity: isMapReady ? 1 : 0,
                marginTop: isMobile ? "20px" : "0",
                "& svg": {
                  height: "auto",
                  width: "100%",
                  maxWidth: "100%",
                  border: "none",
                  "& #icons-overlay": {
                    pointerEvents: "none !important",
                    cursor: "default !important"
                  },
                  "& #icons-overlay path": {
                    pointerEvents: "none !important",
                    fillOpacity: "0.8 !important",
                    strokeOpacity: "0 !important"
                  },
                  "& #labels-overlay": {
                    pointerEvents: "none !important",
                    cursor: "default !important"
                  },
                  "& #labels-overlay text": {
                    pointerEvents: "none !important",
                    userSelect: "none"
                  }
                },
                 "& path": {
                   stroke: "#8a8a8a",
                   strokeWidth: "1",
                   strokeOpacity: "1",
                   opacity: "1",
                   cursor: "pointer",
                   pointerEvents: "all !important",
                   WebkitTapHighlightColor: "rgba(0, 0, 0, 0)",
                   WebkitUserSelect: "none",
                   WebkitTouchCallout: "none",
                   transition: "stroke-width 0.1s cubic-bezier(0.4, 0, 0.2, 1), filter 0.1s cubic-bezier(0.4, 0, 0.2, 1)"
                 },
                 "& .interactive-room": {
                   pointerEvents: "all !important",
                   cursor: "pointer !important"
                 },
                 "& .structure": {
                   pointerEvents: "none !important",
                   cursor: "default !important"
                 }
              }}
              dangerouslySetInnerHTML={{ __html: svgContent }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "60vh",
                width: isMobile ? "90%" : "100%", // Match SVG container width
                background: "linear-gradient(180deg, #1a1a1a 0%, #0f0f0f 100%)",
                border: "2px dashed #4a4a4a",
                borderRadius: 2,
                p: 4
              }}
            >
              <Typography variant="h6" sx={{ color: '#ffffff' }} gutterBottom>
                Floor Plan Not Available
              </Typography>
              <Typography variant="body2" sx={{ color: '#ffffff' }} align="center">
                The floor plan for {currentFloor} is not yet available.<br />
                Please check back later or contact support.
              </Typography>
            </Box>
          )}
        </Box>

        {isSearchingRoom && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}
          >
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                backgroundColor: 'rgba(42, 42, 42, 0.75)',
                border: '1px solid rgba(74, 74, 74, 0.6)',
                backdropFilter: 'blur(12px)',
                maxWidth: 320,
                width: '90%',
                color: '#ffffff'
              }}
            >
              <CircularProgress 
                size={48} 
                sx={{ 
                  mb: 2,
                  color: 'primary.main'
                }} 
              />
              <Typography variant="h6" gutterBottom sx={{ color: '#ffffff', fontWeight: 'medium' }}>
                Finding Your Room...
              </Typography>
            </Paper>
          </Box>
        )}

        {previousRoom && (
          <Box sx={getPopupWrapperStyle()}>
            <Slide 
              direction="down" 
              in={showPreviousRoom && !previousRoomSliding} 
              timeout={400}
              mountOnEnter 
              unmountOnExit
            >
              <Paper 
                key={`previous-room-${previousRoom.id}`}
                sx={getPopupStyle()}
              >
             <Box sx={{ position: "relative", mb: isMobile ? 1 : 1 }}>
               {isMobile ? (
                 <>
                   <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', textAlign: 'center', width: '100%' }}>
                     {previousRoom.name}
                   </Typography>
                   <IconButton
                     onClick={handleRoomClose}
                     sx={{
                       width: 28,
                       height: 28,
                       backgroundColor: 'rgba(74, 74, 74, 0.8)',
                       border: '1px solid rgba(74, 74, 74, 0.8)',
                       color: '#ffffff',
                       fontSize: '0.9rem',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       '&:hover': { 
                         backgroundColor: 'rgba(244, 67, 54, 0.8)',
                         borderColor: 'rgba(244, 67, 54, 0.8)',
                         color: '#ffffff'
                       },
                       position: 'absolute',
                       right: 0,
                       top: 0,
                       lineHeight: 1,
                       paddingTop: '4px'
                     }}
                     size="small"
                   >
                     ×
                   </IconButton>
                 </>
               ) : (
                 <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                   <Typography variant="h6" gutterBottom>
                     {previousRoom.name}
                   </Typography>
                   <IconButton
                     onClick={handleRoomClose}
                     sx={{
                       width: 32,
                       height: 32,
                       backgroundColor: 'rgba(74, 74, 74, 0.8)',
                       border: '1px solid rgba(74, 74, 74, 0.8)',
                       color: '#ffffff',
                       fontSize: '1.4rem',
                       fontWeight: 'bold',
                       '&:hover': { 
                         backgroundColor: 'rgba(244, 67, 54, 0.8)',
                         borderColor: 'rgba(244, 67, 54, 0.8)',
                         color: '#ffffff'
                       },
                       ml: 1
                     }}
                     size="small"
                   >
                     ×
                   </IconButton>
                 </Box>
               )}
             </Box>
            
            {isMobile ? (
              <>
                <Grid container spacing={1.5} sx={{ mb: 1 }}>
                  <Grid item xs={4}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#ffffff', mb: 0.5, fontSize: '0.8rem' }} display="block">
                        Type
                      </Typography>
                      <Chip 
                        label={previousRoom.type} 
                        size="medium"
                        color="secondary" 
                        variant="outlined"
                        sx={{ fontSize: '0.8rem', height: 28 }}
                      />
                    </Box>
                  </Grid>
                  {hasValidStatus(previousRoom) && (
                    <Grid item xs={4}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#ffffff', mb: 0.5, fontSize: '0.8rem' }} display="block">
                          Status
                        </Typography>
                        <Chip 
                          label={previousRoom.status} 
                          size="medium"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.8rem', 
                            height: 28,
                            color: '#ffffff',
                            borderColor: 
                              previousRoom.status === "available" ? "#A8DDA8" :
                              previousRoom.status === "occupied" ? "#FFB3B3" :
                              previousRoom.status === "unavailable" ? "#D4C5E2" : "#9B9B9B"
                          }}
                        />
                      </Box>
                    </Grid>
                  )}
                  {hasValidCapacity(previousRoom) && (
                    <Grid item xs={4}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#ffffff', mb: 0.5, fontSize: '0.8rem' }} display="block">
                          Capacity
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.9rem' }}>
                          {previousRoom.capacity} people
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
                
                {hasValidDescription(previousRoom) && (
                  <Typography variant="body2" sx={{ color: '#ffffff', mb: 1.5, fontSize: '0.9rem', lineHeight: 1.4 }} gutterBottom>
                    {previousRoom.description}
                  </Typography>
                )}
              </>
            ) : (
              <Box sx={{ mb: 0.5 }}>
                {hasValidDescription(previousRoom) && (
                  <Typography variant="body2" sx={{ color: '#ffffff', mb: 1.5, lineHeight: 1.4 }}>
                    {previousRoom.description}
                  </Typography>
                )}
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#ffffff', minWidth: 'fit-content' }}>
                      Type:
                    </Typography>
                    <Chip 
                      label={previousRoom.type} 
                      size="small"
                      color="secondary" 
                      variant="outlined"
                    />
                  </Box>
                  
                  {hasValidStatus(previousRoom) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ color: '#ffffff', minWidth: 'fit-content' }}>
                        Status:
                      </Typography>
                      <Chip 
                        label={previousRoom.status} 
                        size="small"
                        color={
                          previousRoom.status === "available" ? "success" :
                          previousRoom.status === "occupied" ? "error" :
                          previousRoom.status === "unavailable" ? "warning" : "default"
                        }
                        variant="outlined"
                      />
                    </Box>
                  )}
                  
                  {hasValidCapacity(previousRoom) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ color: '#ffffff', minWidth: 'fit-content' }}>
                        Capacity:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {previousRoom.capacity} people
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            </Paper>
          </Slide>
          </Box>
        )}

        {selectedRoom && (
          <Box sx={getPopupWrapperStyle()}>
            <Slide 
              direction="up" 
              in={roomPopupVisible} 
              timeout={300}
              mountOnEnter 
              unmountOnExit
            >
              <Paper 
                key={`current-room-${selectedRoom.id}`}
                ref={popupRef}
                sx={getPopupStyle()}
              >
                <Box sx={{ position: "relative", mb: isMobile ? 1 : 1 }}>
               {isMobile ? (
                 <>
                   <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', textAlign: 'center', width: '100%' }}>
                     {selectedRoom.name}
                   </Typography>
                   {(selectedRoom.type === 'laboratory' || selectedRoom.type === 'classroom') && (
                     <IconButton
                       onClick={handlePanelToggle}
                       sx={{
                         width: 28,
                         height: 28,
                         backgroundColor: 'rgba(74, 74, 74, 0.8)',
                         border: '1px solid rgba(74, 74, 74, 0.8)',
                         color: '#ffffff',
                         fontSize: '1.2rem',
                         fontWeight: 'bold',
                         position: 'absolute',
                         left: 0,
                         top: 0,
                         '&:hover': {
                           backgroundColor: 'rgba(255, 255, 255, 0.1)',
                           borderColor: 'rgba(255, 255, 255, 0.3)'
                         }
                       }}
                       size="small"
                     >
                       <UpArrowIcon 
                         sx={{ 
                           fontSize: '1rem',
                           transform: isPanelExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                           transition: 'transform 0.4s ease-in-out'
                         }} 
                       />
                     </IconButton>
                   )}
                   <IconButton
                     onClick={handleRoomClose}
                     sx={{
                       width: 28,
                       height: 28,
                       backgroundColor: 'rgba(74, 74, 74, 0.8)',
                       border: '1px solid rgba(74, 74, 74, 0.8)',
                       color: '#ffffff',
                       fontSize: '0.9rem',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       position: 'absolute',
                       right: 0,
                       top: 0,
                       '&:hover': {
                         backgroundColor: 'rgba(244, 67, 54, 0.8)',
                         borderColor: 'rgba(244, 67, 54, 0.8)'
                       },
                       lineHeight: 1,
                       paddingTop: '4px'
                     }}
                     size="small"
                   >
                     ×
                   </IconButton>
                 </>
               ) : (
                 <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                   <Typography variant="h6" gutterBottom>
                     {selectedRoom.name}
                   </Typography>
                   <Box sx={{ display: 'flex', gap: 1 }}>
                     {(selectedRoom.type === 'laboratory' || selectedRoom.type === 'classroom') && (
                       <IconButton
                         onClick={handlePanelToggle}
                         sx={{
                           width: 32,
                           height: 32,
                           backgroundColor: 'rgba(74, 74, 74, 0.8)',
                           border: '1px solid rgba(74, 74, 74, 0.8)',
                           color: '#ffffff',
                           fontSize: '1.4rem',
                           fontWeight: 'bold',
                           '&:hover': {
                             backgroundColor: 'rgba(255, 255, 255, 0.1)',
                             borderColor: 'rgba(255, 255, 255, 0.3)'
                           }
                         }}
                         size="small"
                       >
                         <UpArrowIcon 
                           sx={{ 
                             fontSize: '1.2rem',
                             transform: isPanelExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                             transition: 'transform 0.4s ease-in-out'
                           }} 
                           />
                       </IconButton>
                     )}
                     <IconButton
                       onClick={handleRoomClose}
                       sx={{
                         width: 32,
                         height: 32,
                         backgroundColor: 'rgba(74, 74, 74, 0.8)',
                         border: '1px solid rgba(74, 74, 74, 0.8)',
                         color: '#ffffff',
                         fontSize: '1.4rem',
                         fontWeight: 'bold',
                         '&:hover': { 
                           backgroundColor: 'rgba(244, 67, 54, 0.8)',
                           borderColor: 'rgba(244, 67, 54, 0.8)',
                           color: '#ffffff'
                         }
                       }}
                       size="small"
                     >
                       ×
                     </IconButton>
                   </Box>
                 </Box>
               )}
             </Box>
            
            {isMobile ? (
              <>
                <Grid container spacing={1.5} sx={{ mb: 1 }}>
                  <Grid item xs={3.6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#ffffff', mb: 0.5, fontSize: '0.8rem' }} display="block">
                        Type
                      </Typography>
                      <Chip 
                        label={selectedRoom.type} 
                        size="medium"
                        color="secondary" 
                        variant="outlined"
                        sx={{ fontSize: '0.8rem', height: 28 }}
                      />
                    </Box>
                  </Grid>
                  {hasValidCapacity(selectedRoom) && (
                    <Grid item xs={3.6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#ffffff', mb: 0.5, fontSize: '0.8rem' }} display="block">
                          Capacity
                        </Typography>
                        <Chip 
                          label={`${selectedRoom.capacity} people`} 
                          size="medium"
                          color="primary" 
                          variant="outlined"
                          sx={{ fontSize: '0.8rem', height: 28 }}
                        />
                      </Box>
                    </Grid>
                  )}
                  {hasValidStatus(selectedRoom) && (
                    <Grid item xs={4.8}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#ffffff', mb: 0.5, fontSize: '0.8rem' }} display="block">
                          Status
                        </Typography>
                        <Chip 
                          label={selectedRoom.status} 
                          size="medium"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.8rem', 
                            height: 28,
                            color: '#ffffff',
                            borderColor: 
                              selectedRoom.status === "available" ? "#A8DDA8" :
                              selectedRoom.status === "occupied" ? "#FFB3B3" :
                              selectedRoom.status === "unavailable" ? "#D4C5E2" : "#9B9B9B"
                          }}
                        />
                        {/* Show "occupied until" for occupied rooms */}
                        {selectedRoom.status === "occupied" && occupiedUntilTime && (
                          <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5, fontSize: '0.75rem', fontWeight: 'medium' }}>
                            <AccessTimeIcon sx={{ fontSize: '0.75rem', mr: 0.25, verticalAlign: 'middle ' }} />
                            Until {convertTo12Hour(occupiedUntilTime)}
                          </Typography>
                        )}
                        {/* Show "occupied at" for available rooms */}
                        {selectedRoom.status === "available" && nextOccupiedTime && (
                          <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5, fontSize: '0.75rem', fontWeight: 'medium' }}>
                            <AccessTimeIcon sx={{ fontSize: '0.75rem', mr: 0.25, verticalAlign: 'sub' }} />
                            Occupied at {convertTo12Hour(nextOccupiedTime)}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {(selectedRoom.type === 'laboratory' || selectedRoom.type === 'classroom') && (
                  <Collapse 
                    in={isPanelExpanded} 
                    timeout={300}
                  >
                    <Box sx={{ 
                      pt: 1.5, 
                      borderTop: '1px solid', 
                      borderColor: 'divider',
                      height: '250px',
                      overflow: 'auto'
                    }}>
                      {hasValidDescription(selectedRoom) && (
                        <Typography variant="body2" sx={{ color: '#ffffff', mb: 1.5, fontSize: '0.9rem', lineHeight: 1.4 }} gutterBottom>
                          {selectedRoom.description}
                        </Typography>
                      )}
                      <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, fontSize: '0.95rem', fontWeight: 'medium', textAlign: 'center' }}>
                        Room Schedule
                      </Typography>
                      
                      {/* Weekday selector */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                        <ToggleButtonGroup
                          value={selectedWeekday}
                          exclusive
                          onChange={handleWeekdayChange}
                          size="small"
                          sx={{ flexWrap: 'wrap' }}
                        >
                          {getWeekdayOptions().map((day) => (
                            <ToggleButton 
                              key={day.value} 
                              value={day.value}
                              sx={{
                                fontSize: '0.9rem',
                                minWidth: '50px',
                                height: '40px',
                                px: 1.5,
                                color: '#ffffff',
                                backgroundColor: 'transparent',
                                border: '1px solid rgba(74, 74, 74, 0.5)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  borderColor: 'rgba(255, 255, 255, 0.3)'
                                },
                                '&.Mui-selected': {
                                  backgroundColor: 'primary.main',
                                  color: 'primary.contrastText',
                                  borderColor: 'primary.main',
                                  '&:hover': {
                                    backgroundColor: 'primary.dark',
                                    borderColor: 'primary.dark'
                                  }
                                }
                              }}
                            >
                              {day.label}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Box>
                      
                      {/* Schedule display */}
                      {scheduleLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={20} />
                        </Box>
                      ) : roomSchedule && roomSchedule[selectedWeekday] && roomSchedule[selectedWeekday].length > 0 ? (
                        <List dense sx={{ py: 0 }}>
                          {roomSchedule[selectedWeekday].map((entry, index) => (
                            <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '60%', mx: 'auto' }}>
                                <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 'medium' }}>
                                  {convertTo12Hour(entry.start_time)} - {convertTo12Hour(entry.end_time)}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#ffffff',
                                    fontSize: '0.85rem', 
                                    fontWeight: 'medium',
                                    cursor: 'pointer',
                                    '&:hover': { 
                                      color: 'primary.main', 
                                      textDecoration: 'underline' 
                                    }
                                  }}
                                  onClick={() => handleClassCodeClick(entry.course_prefix, entry.course_number)}
                                >
                                  {entry.course_prefix} {entry.course_number}
                                </Typography>
                              </Box>
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#ffffff', fontSize: '0.85rem', fontStyle: 'italic', py: 1, textAlign: 'center' }}>
                          No classes scheduled for {selectedWeekday.toLowerCase()}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                )}
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#ffffff', minWidth: 'fit-content' }}>
                      Type:
                    </Typography>
                    <Chip 
                      label={selectedRoom.type} 
                      size="small"
                      color="secondary" 
                      variant="outlined"
                    />
                  </Box>
                  
                  {hasValidCapacity(selectedRoom) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#ffffff', minWidth: 'fit-content' }}>
                        Capacity:
                      </Typography>
                      <Chip 
                        label={`${selectedRoom.capacity} people`} 
                        size="small"
                        color="primary" 
                        variant="outlined"
                      />
                    </Box>
                  )}
                  
                  {hasValidStatus(selectedRoom) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ color: '#ffffff', minWidth: 'fit-content' }}>
                        Status:
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Chip 
                        label={selectedRoom.status} 
                        size="small"
                        variant="outlined"
                        sx={{
                          color: '#ffffff',
                          borderColor: 
                            selectedRoom.status === "available" ? "#A8DDA8" :
                            selectedRoom.status === "occupied" ? "#FFB3B3" :
                            selectedRoom.status === "unavailable" ? "#D4C5E2" : "#9B9B9B"
                        }}
                      />
                        {/* Show "occupied until" for occupied rooms */}
                        {selectedRoom.status === "occupied" && occupiedUntilTime && (
                          <Typography variant="caption" color="error.main" sx={{ mt: 0.25, fontSize: '0.75rem', fontWeight: 'medium' }}>
                            <AccessTimeIcon sx={{ fontSize: '0.75rem', mr: 0.25, verticalAlign: 'sub' }} />
                            Until {convertTo12Hour(occupiedUntilTime)}
                          </Typography>
                        )}
                        {/* Show "occupied at" for available rooms */}
                        {selectedRoom.status === "available" && nextOccupiedTime && (
                          <Typography variant="caption" color="warning.main" sx={{ mt: 0.25, fontSize: '0.75rem', fontWeight: 'medium' }}>
                            <AccessTimeIcon sx={{ fontSize: '0.75rem', mr: 0.25, verticalAlign: 'sub' }} />
                            Occupied at {convertTo12Hour(nextOccupiedTime)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>

                {(selectedRoom.type === 'laboratory' || selectedRoom.type === 'classroom') && (
                  <Collapse 
                    in={isPanelExpanded} 
                    timeout={300}
                  >
                    <Box sx={{ 
                      pt: 1.5, 
                      borderTop: '1px solid', 
                      borderColor: 'divider',
                      height: '300px',
                      overflow: 'auto'
                    }}>
                      {hasValidDescription(selectedRoom) && (
                        <Typography variant="body2" sx={{ color: '#ffffff', mb: 1.5, lineHeight: 1.4 }} gutterBottom>
                          {selectedRoom.description}
                        </Typography>
                      )}
                      <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, fontWeight: 'medium', textAlign: 'center' }}>
                        Room Schedule
                      </Typography>
                      
                      {/* Weekday selector */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                        <ToggleButtonGroup
                          value={selectedWeekday}
                          exclusive
                          onChange={handleWeekdayChange}
                          size="small"
                          sx={{ flexWrap: 'wrap' }}
                        >
                          {getWeekdayOptions().map((day) => (
                            <ToggleButton 
                              key={day.value} 
                              value={day.value}
                              sx={{
                                fontSize: '0.8rem',
                                minWidth: '45px',
                                height: '32px',
                                px: 1.5,
                                color: '#ffffff',
                                backgroundColor: 'transparent',
                                border: '1px solid rgba(74, 74, 74, 0.5)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  borderColor: 'rgba(255, 255, 255, 0.3)'
                                },
                                '&.Mui-selected': {
                                  backgroundColor: 'primary.main',
                                  color: 'primary.contrastText',
                                  borderColor: 'primary.main',
                                  '&:hover': {
                                    backgroundColor: 'primary.dark',
                                    borderColor: 'primary.dark'
                                  }
                                }
                              }}
                            >
                              {day.label}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Box>
                      
                      {/* Schedule display */}
                      {scheduleLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : roomSchedule && roomSchedule[selectedWeekday] && roomSchedule[selectedWeekday].length > 0 ? (
                        <List dense sx={{ py: 0 }}>
                          {roomSchedule[selectedWeekday].map((entry, index) => (
                            <ListItem key={index} sx={{ px: 0, py: 0.75 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '60%', mx: 'auto' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {convertTo12Hour(entry.start_time)} - {convertTo12Hour(entry.end_time)}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#ffffff',
                                    fontWeight: 'medium',
                                    cursor: 'pointer',
                                    '&:hover': { 
                                      color: 'primary.main', 
                                      textDecoration: 'underline' 
                                    }
                                  }}
                                  onClick={() => handleClassCodeClick(entry.course_prefix, entry.course_number)}
                                >
                                  {entry.course_prefix} {entry.course_number}
                                </Typography>
                              </Box>
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#ffffff', fontStyle: 'italic', py: 1, textAlign: 'center' }}>
                          No classes scheduled for {selectedWeekday.toLowerCase()}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                )}
              </>
            )}
            </Paper>
          </Slide>
          </Box>
        )}
      </Box>
    </div>
  );
};

export default MapPage;
